import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import MainContainerWithWindow from '../../../../../../components/MainContainerWithWindow/MainContainerWithWindow';
import { Loading } from '../../../../../../components/Loading/Loading';
import { getRequestErrorMessage, pushNotify } from '../../../../../../components/Notifications';
import { useEscapeKey } from '../../../../../../utils/hooks/useEscapeKey';
import { useCreateDisputeMutation } from '../../../../../../redux/services/MerchantApi';
import styles from './CreateDispute.module.css';

interface CreateDisputeProps {
  orderId?: string;
  withdrawalId?: string;
  initialAmount?: string | number | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface DisputeInputs {
  new_real_income: string;
  comment:         string;
}

export const CreateDispute: React.FC<CreateDisputeProps> = ({
  orderId,
  withdrawalId,
  initialAmount,
  onClose,
  onSuccess,
}) => {
  const [createDispute, { isLoading, isError }] = useCreateDisputeMutation();
  const methods = useForm<DisputeInputs>({
    mode: 'onSubmit',
    defaultValues: { new_real_income: initialAmount ? String(initialAmount) : '', comment: '' },
  });
  const { register, handleSubmit, reset, setError, formState } = methods;

  const [files, setFiles] = useState<File[]>([]);
  const [filesError, setFilesError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const close = () => { reset(); setFiles([]); setFilesError(null); onClose(); };
  useEscapeKey(true, close);

  const onFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;
    setFiles(prev => [...prev, ...Array.from(fileList)]);
    setFilesError(null);
    e.target.value = '';
  };

  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const onSubmit: SubmitHandler<DisputeInputs> = async data => {
    // бэкенд требует хотя бы один файл (чек или скриншот): без него 400 {"files": ["Обязательное поле."]}
    if (!files.length) {
      setFilesError('Прикрепите хотя бы один файл: чек или скриншот перевода.');
      return;
    }
    const formData = new FormData();
    // передаём simple string: либо order, либо withdrawal
    if (orderId) {
      formData.append('order', orderId);
    } else if (withdrawalId) {
      formData.append('withdrawal', withdrawalId);
    }
    formData.append('new_real_income', data.new_real_income);
    formData.append('comment',         data.comment);

    files.forEach(file => formData.append('files', file));

    try {
      await createDispute(formData).unwrap();
      pushNotify('Спор открыт', 'Готово');
      onSuccess();
      reset();
      setFiles([]);
    } catch (error) {
      const data = (error as { data?: Record<string, unknown> } | undefined)?.data;
      const fieldErrors = data && typeof data === 'object' ? data : {};
      const fileMessage = Array.isArray(fieldErrors.files) ? String(fieldErrors.files[0]) : null;
      const amountMessage = Array.isArray(fieldErrors.new_real_income) ? String(fieldErrors.new_real_income[0]) : null;
      const message = getRequestErrorMessage(error, 'Не удалось открыть спор');
      pushNotify(fileMessage ? `Файлы: ${fileMessage}` : amountMessage ? `Сумма: ${amountMessage}` : message, 'Ошибка');
      if (fileMessage) setFilesError(fileMessage);
      if (amountMessage || !fileMessage) setError('new_real_income', { type: 'manual', message: amountMessage || message });
    }
  };

  if (isLoading) {
    return createPortal(
      <MainContainerWithWindow><Loading /></MainContainerWithWindow>,
      document.body
    );
  }
  return createPortal(
    <div className={styles.backdrop} onClick={close}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="create-dispute-title" onClick={e => e.stopPropagation()}>
        <button type="button" aria-label="Закрыть" className={styles.closeBtn} onClick={close}>
          ×
        </button>
        <h2 id="create-dispute-title" className={styles.title}>Открыть спор</h2>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            {/* Сумма диспута */}
            <div className={styles.field}>
              <label>Сумма спора</label>
              <input
                {...register('new_real_income', { required: 'Введите сумму' })}
                type="text"
                placeholder="Введите сумму"
              />
              {formState.errors.new_real_income && (
                <span className={styles.error}>
                  {formState.errors.new_real_income.message}
                </span>
              )}
            </div>

            {/* Комментарий */}
            <div className={styles.field}>
              <label>Комментарий</label>
              <textarea
                {...register('comment', { required: 'Введите комментарий' })}
                placeholder="Опишите суть спора"
                rows={4}
              />
              {formState.errors.comment && (
                <span className={styles.error}>
                  {formState.errors.comment.message}
                </span>
              )}
            </div>

            {/* Загрузка файлов */}
            <div className={styles.field}>
              <label>Прикрепите документ (обязательно)</label>
              <div className={styles.dropzone} onClick={() => inputRef.current?.click()}>
                + Выбрать файл
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={onFilesChange}
                />
              </div>
              {filesError && <span className={styles.error} role="alert">{filesError}</span>}
              <ul className={styles.fileList}>
                {files.map((f, i) => (
                  <li key={i} className={styles.fileItem}>
                    <span className={styles.fileName}>{f.name}</span>
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => removeFile(i)}
                    >
                      –
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Кнопки */}
            <div className={styles.actions}>
              {isError && <span className={styles.error}>Не удалось открыть спор. Проверьте данные и повторите попытку.</span>}
              <button type="button" className={styles.cancelBtn} onClick={close}>
                Закрыть
              </button>
              <button type="submit" className={styles.submitBtn}>
                Продолжить
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>,
    document.body
  );
};

export default CreateDispute;
